import { useEffect, useRef, useState } from "react";

import { doLogout, refreshAccessToken } from "@/api/config";
import { STATUS_CODE } from "@/constants/errorCode";
import {
  CustomError,
  DATASET_STREAM_MAX_RETRIES,
  UnauthorizedStreamError
} from "@/utils/errors";

import type { EventSourceMessage } from "@microsoft/fetch-event-source";
import { fetchEventSource } from "@microsoft/fetch-event-source";

interface UseEventSourceResult<T = string> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  close: () => void;
}

type AnyRecord = Record<string, unknown>;

type EventControls = { close: () => void };

type EventHandlers<M extends AnyRecord> = {
  [K in keyof M]: (
    data: M[K],
    event: EventSourceMessage,
    controls?: EventControls
  ) => void | Promise<void>;
};

type EventSourceOptions<M extends AnyRecord> = {
  /**
   * Stable key identifying the stream. Any variable request inputs that
   * should trigger a reconnect when changed (e.g. body/headers) must be
   * encoded here — request props (url/method/headers/body) captured mid-stream
   * otherwise stay pinned for the life of the connection. Defaults to `url`.
   */
  eventKey?: string | Array<unknown>;
  url: string;
  enabled?: boolean;
  eventHandlers: EventHandlers<M>;
  onError?: (error: CustomError) => void;
  onOpen?: () => void;
  /**
   * Fired once when the stream is terminally closed: unmount, consumer
   * `close()`, a `closeOn` event, or retry budget exhaustion. Not fired on
   * transient reconnect/retry aborts.
   */
  onClose?: () => void;
  /** Fired when the retry budget is exhausted. Use to surface UX (e.g. a toast). */
  onRetriesExhausted?: (error: CustomError) => void;
  closeOn?: Array<keyof M & string>;
  parse?: (data: string) => M[keyof M];
  /** Delay in ms between reconnect attempts. */
  retryInterval?: number;
  maxRetries?: number;
  body?: string;
  method?: "GET" | "POST";
  headers?: Record<string, string>;
};

const computeCacheKey = (
  key: string | Array<unknown> | undefined,
  fallback: string
): string => (Array.isArray(key) ? JSON.stringify(key) : (key ?? fallback));

// Coerce any thrown value into a CustomError so consumers can rely on
// `code` and `reason` being present on the error object passed to onError.
const toCustomError = (err: unknown): CustomError => {
  if (err instanceof CustomError) return err;
  if (err instanceof Error) return new CustomError(err.message, 0);
  return new CustomError(String(err), 0);
};

/**
 * useEventSource wraps @microsoft/fetch-event-source for GET/POST SSE with
 * 401 token-refresh, bounded retries at a fixed interval, and lifecycle-safe
 * callbacks.
 */
function useEventSource<M extends AnyRecord = Record<string, string>>({
  eventKey,
  url,
  enabled = true,
  eventHandlers,
  closeOn,
  retryInterval = 5000,
  maxRetries = DATASET_STREAM_MAX_RETRIES,
  body,
  method = "GET",
  headers,
  onError,
  onOpen,
  onClose,
  onRetriesExhausted,
  parse
}: EventSourceOptions<M>): UseEventSourceResult<M[keyof M]> {
  const [data, setData] = useState<M[keyof M] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const controllerRef = useRef<AbortController | null>(null);

  const latestRef = useRef({
    eventHandlers,
    parse,
    closeOn,
    onOpen,
    onError,
    onClose,
    onRetriesExhausted
  });

  latestRef.current = {
    eventHandlers,
    parse,
    closeOn,
    onOpen,
    onError,
    onClose,
    onRetriesExhausted
  };

  // Connection state kept in refs so async callbacks (fetch-event-source
  // handlers, setTimeout, refreshAccessToken) always see the latest value
  // without forcing a re-render:
  // - shouldReconnectRef: guards against reconnect-after-close races when
  //   a Promise or scheduled retry resolves after the consumer called close().
  // - retryTimeoutRef:   handle held so a pending reconnect setTimeout can be
  //   cancelled on close or 401-refresh (prevents zombie reconnects).
  // - didRefreshTokenRef: one-shot — refresh the token at most once per
  //   connection. A second 401 after refresh means the session is dead, so
  //   we log the user out instead of looping refresh attempts.
  // - retryCountRef:     consecutive-failure count. Bounds retries via
  //   maxRetries and drives the exponential-backoff delay.
  const shouldReconnectRef = useRef(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didRefreshTokenRef = useRef(false);
  const retryCountRef = useRef(0);

  const cacheKey = computeCacheKey(eventKey, url);

  const abortConnection = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  };

  const clearPendingRetry = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  const reportError = (err: unknown) => {
    setError(err as Error);
    latestRef.current.onError?.(toCustomError(err));
    abortConnection();
  };

  const closeAndDisableReconnect = () => {
    if (!shouldReconnectRef.current) return;
    shouldReconnectRef.current = false;
    clearPendingRetry();
    abortConnection();
    setIsLoading(false);
    latestRef.current.onClose?.();
  };

  const connect = () => {
    setIsLoading(true);
    setError(null);

    const controller = new AbortController();
    controllerRef.current = controller;

    const handleOpen = async (response: Response) => {
      if (response.ok) {
        setIsLoading(false);
        setError(null);
        retryCountRef.current = 0;
        didRefreshTokenRef.current = false;
        latestRef.current.onOpen?.();
        return;
      }

      if (response.status === STATUS_CODE.UNAUTHORIZED) {
        throw new UnauthorizedStreamError();
      }

      // Read the body here — onerror only receives an Error, so the Response
      // (and its payload like { error: { code, reason, message } }) is gone
      // after this point.
      const payload = (await response
        .clone()
        .json()
        .catch(() => null)) as {
        error?: { reason?: string; message?: string; details?: unknown };
      } | null;
      const beError = payload?.error;

      throw new CustomError(
        beError?.message ?? `Unexpected response status: ${response.status}`,
        response.status,
        beError?.details,
        beError?.reason
      );
    };

    const handleMessage = (msg: EventSourceMessage) => {
      const { event, data: raw } = msg;
      if (!event) return;

      const handler = latestRef.current.eventHandlers[event as keyof M];
      if (!handler) return;

      let parsed: M[keyof M];

      try {
        parsed =
          latestRef.current.parse && typeof raw === "string"
            ? latestRef.current.parse(raw)
            : (raw as M[keyof M]);
      } catch (err) {
        reportError(err);
        return;
      }

      setData(parsed);

      const maybeClose = () => {
        if (latestRef.current.closeOn?.includes(event as keyof M & string)) {
          closeAndDisableReconnect();
        }
      };

      let handlerResult: void | Promise<void>;

      try {
        handlerResult = handler(parsed, msg, {
          close: closeAndDisableReconnect
        });
      } catch (err) {
        reportError(err);
        return;
      }

      // If the handler is async, wait for it before maybeClose so a
      // closeOn-triggered teardown (and onClose) doesn't race with in-flight
      // work; .catch routes async rejections through reportError the same way
      // the try/catch above handles synchronous throws.
      if (handlerResult instanceof Promise) {
        handlerResult.then(maybeClose).catch(reportError);
      } else {
        maybeClose();
      }
    };

    const handleUnauthorized = async () => {
      if (didRefreshTokenRef.current) {
        closeAndDisableReconnect();
        await doLogout();
        return;
      }

      didRefreshTokenRef.current = true;
      clearPendingRetry();
      abortConnection();
      refreshAccessToken()
        .then(() => {
          if (!shouldReconnectRef.current) return;
          connect();
        })
        .catch((refreshErr: Error) => {
          if (!shouldReconnectRef.current) return;
          setError(refreshErr);
          setIsLoading(false);
          latestRef.current.onError?.(toCustomError(refreshErr));
        });
    };

    const handleGenericError = (err: Error) => {
      reportError(err);
      retryCountRef.current++;

      if (retryCountRef.current >= maxRetries) {
        latestRef.current.onRetriesExhausted?.(toCustomError(err));
        closeAndDisableReconnect();
        return;
      }

      if (shouldReconnectRef.current) {
        retryTimeoutRef.current = setTimeout(() => {
          connect();
        }, retryInterval);
      }
    };

    const handleError = (err: Error) => {
      if (err instanceof UnauthorizedStreamError) {
        void handleUnauthorized().catch(reportError);
        throw err;
      }
      handleGenericError(err);
      // Throw so fetchEventSource rejects and skips its built-in retry loop;
      // our custom backoff via setTimeout(connect) is the single source of retries.
      throw err;
    };

    fetchEventSource(url, {
      method,
      signal: controller.signal,
      credentials: "include",
      headers,
      body,
      openWhenHidden: true,
      onopen: handleOpen,
      onmessage: handleMessage,
      onerror: handleError
    });
  };

  useEffect(() => {
    if (!enabled) return;

    if (!url) {
      const err = new Error("EventSource URL is not provided.");
      setError(err);
      setIsLoading(false);
      latestRef.current.onError?.(toCustomError(err));
      return;
    }

    shouldReconnectRef.current = true;
    didRefreshTokenRef.current = false;
    retryCountRef.current = 0;

    connect();

    return () => {
      closeAndDisableReconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, enabled]);

  return { data, error, isLoading, close: closeAndDisableReconnect };
}

export default useEventSource;
